import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Split } from "@/api/accounting.tsx"
import { SplitFormFields } from "./split_components/split_form_fields.tsx"
import { useState, useRef, useEffect } from "preact/hooks"
import { OrgFieldConfig } from "@/api/org_field_config.tsx"
import { UserState } from "@/auth/user.tsx"
import { OrgConfigState } from "@/org_config/state.tsx"

interface FullCodingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (values: Partial<Split>) => void
  selectedTransactionIds: string[]
}

export function FullCodingDialog({ open, onOpenChange, onSubmit, selectedTransactionIds }: FullCodingDialogProps) {
  const user = UserState.use()
  const orgConfig = OrgConfigState.use()
  const popoverOpenRef = useRef(false)
  
  // Use the first transaction ID for promotion selector (they should all be from the same deduction context)
  const firstTransactionId = selectedTransactionIds[0] || ""
  
  const [formValues, setFormValues] = useState<Partial<Split>>({
    amount: undefined,
    account_id: undefined,
    promotion_id: undefined,
    execution_date: undefined,
    promo_type: undefined,
    promo_sub_type: undefined,
    code_description: undefined,
    product_category: undefined,
    retailer_invoice_number: undefined,
  })

  // Monitor for popover elements to detect when they're open
  useEffect(() => {
    if (!open) return

    function checkPopoverOpen() {
      const popoverContent = document.querySelector('[data-radix-popper-content-wrapper]')
      popoverOpenRef.current = !!popoverContent
    }

    const interval = setInterval(checkPopoverOpen, 50)
    return () => clearInterval(interval)
  }, [open])

  function handleUpdate(field: keyof Split, value: any) {
    setFormValues(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  function handleSubmit() {
    // Validate that account_id is provided
    if (!formValues.account_id) {
      return
    }
    onSubmit(formValues)
    // Reset form
    setFormValues({
      amount: undefined,
      account_id: undefined,
      promotion_id: undefined,
      execution_date: undefined,
      promo_type: undefined,
      promo_sub_type: undefined,
      code_description: undefined,
      product_category: undefined,
      retailer_invoice_number: undefined,
    })
    onOpenChange(false)
  }

  function handleCancel() {
    // Reset form
    setFormValues({
      amount: undefined,
      account_id: undefined,
      promotion_id: undefined,
      execution_date: undefined,
      promo_type: undefined,
      promo_sub_type: undefined,
      code_description: undefined,
      product_category: undefined,
      retailer_invoice_number: undefined,
    })
    onOpenChange(false)
  }

  // Create a temporary split object for the form
  const tempSplit: Partial<Split> = {
    id: "bulk-form",
    deduction_id: firstTransactionId,
    ...formValues,
  }

  function handleDialogOpenChange(newOpen: boolean) {
    // If a popover is open, ignore the close request temporarily
    if (!newOpen && popoverOpenRef.current) {
      // Check again after a short delay to see if popover has closed
      setTimeout(() => {
        // Only close if popover is no longer open
        if (!popoverOpenRef.current) {
          onOpenChange(false)
        }
      }, 150)
      return
    }
    
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent 
        className="max-w-3xl max-h-[85vh] flex flex-col"
        onInteractOutside={(e) => {
          // Prevent closing when clicking on popover content (AutocompleteSelect uses Popover)
          const target = e.target as HTMLElement
          // Check if the click is on a popover or command component
          if (target.closest('[data-radix-popper-content-wrapper]') || 
              target.closest('[role="listbox"]') ||
              target.closest('[cmdk-list]') ||
              target.closest('[cmdk-input]') ||
              target.closest('[cmdk-item]')) {
            e.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Fully Code {selectedTransactionIds.length} Transaction{selectedTransactionIds.length > 1 ? "s" : ""}</DialogTitle>
          <DialogDescription>
            Fill in the coding fields below. These values will be applied to all selected transactions. The amount will automatically be set to each transaction's invoice amount.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 pr-2">
          <SplitFormFields
            split={tempSplit}
            onUpdate={handleUpdate}
            orgFieldConfigs={orgConfig.orgFieldConfigs}
            orgId={user?.org_id ?? undefined}
            deductionId={firstTransactionId}
            showOptionalFields={true}
            fieldIdPrefix="bulk-"
            hideAmount={true}
            hideProductCategory={true}
            hideRetailerInvoice={true}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!formValues.account_id}
          >
            Set codes on {selectedTransactionIds.length} transaction{selectedTransactionIds.length > 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

